import React from 'react';

import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import EntityFormField from 'src/features/membership/components/forms/EntityFormField';
import { MembershipFormSchemaValues } from 'src/features/membership/formSchema';
import { membershipMessages } from 'src/features/membership/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectTrigger } from 'src/registry/ui/Select';
import { generalMessages } from 'src/registry/utils/messages';
import { Separator } from 'src/registry/ui/Separator';
import { DURATION_UNIT } from 'src/features/membershipType/types';

type MembershipTypeData = {
    name: string;
    duration: number | null;
    durationUnit: DURATION_UNIT | null;
    max_allowed_spaces_count: number | null;
    allow_custom_space_count_definition: boolean | null;
};

type MembershipFormProps = {
    form: UseFormReturn<MembershipFormSchemaValues>;
    onSubmit: (values: MembershipFormSchemaValues) => void;
    membershipTypeData: MembershipTypeData;
    selectedMembershipValidTo?: string | null;
    isEditMode?: boolean | null;
};
function MembershipForm({ onSubmit, form, membershipTypeData, selectedMembershipValidTo, isEditMode }: MembershipFormProps) {
    const { formatMessage } = useIntl();

    const validFrom = form.watch('valid_from');

    React.useEffect(() => {
        const { durationUnit, duration } = membershipTypeData;

        if (selectedMembershipValidTo) {
            form.setValue('valid_to', dayjs(selectedMembershipValidTo).toISOString());
        }

        if (!durationUnit || !duration || !validFrom || selectedMembershipValidTo) return;

        const newValidTo = dayjs(validFrom).add(duration, durationUnit).toISOString();
        form.setValue('valid_to', newValidTo);
    }, [form, membershipTypeData, validFrom, selectedMembershipValidTo]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormItem variant="dialog">
                <FormLabel variant="dialog" required>
                    {formatMessage(membershipMessages.template)}
                </FormLabel>
                <FormControl>
                    <Select disabled>
                        <SelectTrigger className="w-full">{membershipTypeData.name}</SelectTrigger>
                    </Select>
                </FormControl>
            </FormItem>
            <InputFormField<MembershipFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <Separator />
            <EntityFormField form={form} />
            <Separator />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={isEditMode === true ? { before: dayjs().toDate() } : undefined}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabled={!!(membershipTypeData.duration && membershipTypeData.durationUnit)}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {membershipTypeData.max_allowed_spaces_count && !membershipTypeData.allow_custom_space_count_definition && (
                <InputFormField<MembershipFormSchemaValues>
                    form={form}
                    name="max_allowed_spaces_count"
                    label={formatMessage(membershipMessages.maxAllowedSpaces)}
                    type="number"
                    disabled={true}
                    required={false}
                />
            )}
            {membershipTypeData.allow_custom_space_count_definition && (
                <InputFormField<MembershipFormSchemaValues>
                    form={form}
                    name="max_allowed_spaces_count"
                    label={formatMessage(membershipMessages.maxAllowedSpaces)}
                    type="number"
                    required={true}
                />
            )}
        </Form>
    );
}

export default MembershipForm;
